import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/contextoAuth';
import { useConexion } from '../lib/useConexion';
import CambioPassword from '../pages/CambioPassword';
import BarraLateral from './BarraLateral';
import Encabezado from './Encabezado';
import AvisoRechazados from './AvisoRechazados';
import BotonAsistente from './BotonAsistente';
import BotonReportar from '../tickets/BotonReportar';
import Reportar from '../tickets/Reportar';
import { modulosVisibles, moduloInicial } from './modulos';
import { navegacion } from './navegacion';
import { useCapa, useNavegacion } from './navegacionReact';
import { contextoDeUrl, urlDe } from './navegacionContexto';
import AvisoSesion from './AvisoSesion';
import AvisoLicencia from './AvisoLicencia';
import AvisoServicios from './AvisoServicios';
import CintaSoporte from '../consola/CintaSoporte';
import BandaMantenimiento from '../consola/BandaMantenimiento';
import CartelDeModulo from '../escala/CartelDeModulo';
import CartelDeAviso from '../cumplimiento/CartelDeAviso';
import AvisoInstalar from '../integrador/pwa/AvisoInstalar';
import SalidaConPendientes from '../integrador/pwa/SalidaConPendientes';
import { decidirSalida } from '../integrador/pwa/salidaConPendientes';
import { contarTodo } from '../integrador/pwa/pendientes';
import { X } from 'lucide-react';
import { esLaVersionDeEstaPestana, ESPERA_AVISO_MS } from '../integrador/pwa/versionNueva';
import { api } from '../api';
import { useInactividad } from '../lib/useInactividad';
import { devolverBorrador, hayBorrador } from '../lib/borradores';

// Armazón responsivo de la consola. Barra lateral fija en pantalla ancha,
// cajón en teléfono. Reúne los módulos según el rol y monta el activo.

/* =====================================================================
   «HAY UNA VERSIÓN NUEVA DE TEGO — RECARGAR» · TODO-SIRVE Fase 4
   =====================================================================
   El service worker avisa (postMessage `tego:version-nueva`) cuando una
   versión nueva toma el control, y el navegador dispara `controllerchange`.
   Hasta el 19 de septiembre de 2026 no había nada que lo escuchara: la
   pestaña abierta seguía con el paquete de antes hasta que alguien
   recargaba por su cuenta. Un aviso fijo abajo, con un solo botón. La
   primera instalación (sin controlador previo) no avisa: no hay nada
   viejo que dejar.

   Y NO AVISA A LA PESTAÑA QUE YA TIENE LA VERSIÓN NUEVA (DIRECTRIZ-PWA F1,
   corrección P1-2, 11 oct 2026). Desde que el build sella sw.js por
   paquete, cada despliegue instala un worker nuevo también en la pestaña
   que acaba de cargar la versión nueva: `controllerchange` le llegaría
   igual y el aviso mentiría («hay una versión nueva» de la que ya está
   mirando). El worker manda en el aviso la versión que instaló; si es la
   de esta pestaña (`__TEGO_VERSION__`), no hay nada que recargar. El
   `controllerchange` queda como respaldo con una espera corta: si el
   aviso no llega (worker anterior sin versión), se enseña igual. */
function AvisoVersionNueva() {
  const [hay, setHay] = useState(false);
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
    const sw = navigator.serviceWorker;
    const habiaControlador = !!sw.controller;
    let espera = null;
    const porMensaje = (e) => {
      if (e?.data?.tipo !== 'tego:version-nueva' || !habiaControlador) return;
      clearTimeout(espera);
      if (!esLaVersionDeEstaPestana(e.data.version)) setHay(true);
    };
    const porCambio = () => {
      if (!habiaControlador) return;
      clearTimeout(espera);
      espera = setTimeout(() => setHay(true), ESPERA_AVISO_MS);
    };
    sw.addEventListener('message', porMensaje);
    sw.addEventListener('controllerchange', porCambio);
    return () => {
      clearTimeout(espera);
      sw.removeEventListener('message', porMensaje);
      sw.removeEventListener('controllerchange', porCambio);
    };
  }, []);
  if (!hay) return null;
  return (
    <div role="status" className="fixed inset-x-0 bottom-0 z-40 bg-[var(--color-marca)] text-white px-4 py-3 flex items-center justify-between gap-3 shadow-lg">
      <span className="text-sm font-medium">Hay una versión nueva de TEGO.</span>
      <button type="button" onClick={() => window.location.reload()}
              className="shrink-0 rounded-lg bg-white text-[var(--color-marca)] px-3 py-2 text-sm font-semibold">
        Recargar
      </button>
    </div>
  );
}

export default function AppShell() {
  const { usuario, cerrarSesion, tieneNivel, esEmpresa, puede,
          ajustesSesion, renovarSesion } = useAuth();
  const { online, enCola, porRevisar, rechazados, descartarRechazados } = useConexion();
  // DIRECTRIZ-PWA F2 (D-13): cerrar sesión con pendientes avisa antes;
  // nunca borra. `salidaConPendientes` es cuántos hay cuando se preguntó.
  const [salidaConPendientes, setSalidaConPendientes] = useState(0);
  // La franja ámbar «N por revisar» se cierra por sesión.
  const [porRevisarCerrado, setPorRevisarCerrado] = useState(false);

  // EL CIERRE POR INACTIVIDAD · deuda T-b. Los minutos los manda el
  // servidor; aquí no hay ningún número escrito a mano.
  const { segundosRestantes, seguirAqui } = useInactividad({
    inactividadMinutos: ajustesSesion?.inactividad_minutos,
    activo: true,
    alCerrar: useCallback(() => cerrarSesion('INACTIVIDAD'), [cerrarSesion]),
    renovar: renovarSesion,
  });

  // Módulos visibles para el rol y para el tipo de cuenta. La regla vive en
  // `modulos.jsx` para que se pueda probar la de verdad y no una copia.
  const grupos = useMemo(
    () => modulosVisibles(tieneNivel, esEmpresa, puede),
    [tieneNivel, esEmpresa, puede],
  );
  const visibles = grupos.flatMap((g) => g.items);

  // Qué módulo se abre: lo que diga la dirección, si el rol lo permite.
  // El permiso se comprueba SIEMPRE: un enlace a `?m=usuarios` reenviado a
  // un operador no puede montarle una pantalla de administración. Cae al
  // módulo inicial de su rol, sin error ni pantalla en blanco.
  //
  // UNA SOLA PUERTA · TC-DR-AT (3 oct 2026). Qué pantalla está abierta ya
  // no es un estado de este componente: lo dice el módulo de navegación
  // (`navegacion.js`), que es quien apila cada pantalla en el historial
  // del navegador. Por eso «atrás» desde Entrada vuelve a Mi trabajo y no
  // al portal, y recargar deja en la misma pantalla.
  //
  // La pila se siembra AQUÍ, al nacer el estado y no en un efecto: los
  // efectos de las pantallas hijas corren antes que los de este
  // componente, y necesitan encontrar su entrada ya puesta. Sembrar es
  // idempotente (el doble render de desarrollo no añade nada).
  const raiz = moduloInicial(esEmpresa);
  const [inicial] = useState(() => {
    const destino = navegacion.resolverInicial({
      permitido: (id) => visibles.some((m) => m.id === id), raiz, contextoDeUrl,
    });
    navegacion.sembrar({ raiz: { m: raiz }, destino });
    // LA DIRECCIÓN DICE LA PANTALLA, TAMBIÉN RECIÉN ENTRADO · auditoría de
    // TC-DR-AT, nota H7. Tras entrar, la barra seguía diciendo
    // `/app?ir=login` aunque se estuviera en Mi trabajo. Solo se toca esa
    // marca del acceso, y solo si es lo ÚNICO que trae la dirección: los
    // enlaces que leen otros parámetros siguen encontrándolos.
    if (destino.m === raiz && /^\?ir=(login|registro)$/.test(window.location.search)) {
      navegacion.reescribirUrl(urlDe({ m: raiz, ctx: null }, window.location.pathname));
    }
    return destino;
  });
  const { pantalla } = useNavegacion();
  // Contexto con el que se entra al módulo: el SKU que se buscó, la
  // ubicación que se eligió, el movimiento (Entrada, Salida, Traspaso)…
  // Es lo que hace que un resultado de búsqueda abra la ficha concreta en
  // vez de la lista genérica. Viaja en la entrada del historial, y lo
  // barato también en la dirección (`navegacionContexto.js`).
  // AC-15 · si se llegó por la etiqueta de un producto o de una pieza
  // (`/q/…`, `/qp/…`), «Escanear» nace con esa lectura y abre su ficha.
  const { m: activa, ctx: contexto } = pantalla ?? inicial;
  useEffect(() => {
    // Con sesión, la raíz avisa antes de salir; al cerrarla, deja de avisar.
    navegacion.sembrar({ raiz: { m: raiz } });
    return () => navegacion.soltarRaiz();
  }, [raiz]);
  // LO QUE SE ESTABA ESCRIBIENDO, DEVUELTO A SU SITIO · deuda T-b.
  //
  // Al volver a entrar se devuelve lo rescatado, pero DESPUÉS de que la
  // pantalla se haya pintado: escribir en un campo que todavía no existe
  // no coloca nada. Un respiro corto basta y no se nota.
  useEffect(() => {
    if (!hayBorrador()) return undefined;
    const t = setTimeout(() => devolverBorrador(), 300);
    return () => clearTimeout(t);
  }, [activa]);

  // EL AVISO DE LICENCIA Y DE VERSIÓN NUEVA · Bloque 3.
  //
  // Se pide UNA VEZ al entrar y luego cada media hora. No más: el estado
  // de una licencia cambia de día en día, y preguntarlo cada minuto sería
  // ruido en la red del cliente para saber algo que no se ha movido.
  //
  // SI LA PETICIÓN FALLA NO SE PINTA NADA, y es deliberado. Un banner que
  // dijera «no se pudo comprobar la licencia» cada vez que el nodo tiene
  // un mal segundo enseñaría a la gente a ignorar esta franja, que es
  // justo la que tiene que leerse el día que diga algo.
  const [aviso, setAviso] = useState(null);
  useEffect(() => {
    let vivo = true;
    const mirar = () => api.get('/nodo/aviso')
      .then((d) => { if (vivo) setAviso(d); })
      .catch(() => {});
    mirar();
    const t = setInterval(mirar, 30 * 60 * 1000);
    return () => { vivo = false; clearInterval(t); };
  }, []);

  const [cajon, setCajon] = useState(false);
  // El menú del teléfono es una capa: atrás lo cierra y la pantalla de
  // fondo no cambia.
  useCapa(cajon, () => setCajon(false));
  // «Pedir ayuda a TI»: un solo modal, abierto desde la cabecera, el menú o
  // el flotante del teléfono. Vive aquí para compartirse entre los tres.
  const [reportar, setReportar] = useState(false);

  // Defensa (lección de la Fase 4): si el rol no habilita ningún módulo, no
  // se intenta renderizar nada; se muestra una salida segura.
  if (visibles.length === 0) {
    return (
      <div className="consola min-h-dvh bg-[var(--color-lienzo)] flex flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-gray-600">Tu rol no tiene módulos asignados. Contacta a tu administrador.</p>
        <button onClick={() => cerrarSesion('A_MANO')} className="text-sm font-semibold text-[var(--color-marca)]">Cerrar sesión</button>
      </div>
    );
  }

  // La dirección se actualiza ANTES de cambiar de pantalla: así el módulo
  // que se monta lee una barra de direcciones ya limpia de los filtros del
  // anterior, en vez de heredarlos durante un render. Lo hace el módulo de
  // navegación, que además empuja la entrada al historial y, si la
  // pantalla que se deja tiene trabajo sin guardar, pregunta antes.
  const seleccionar = (id, ctx = null) => {
    navegacion.ir({ m: id, ctx });
    setCajon(false);
  };
  const esPassword = activa === '__password';
  const modulo = visibles.find((m) => m.id === activa) || visibles[0];
  const Pantalla = esPassword ? null : modulo.Comp;
  const titulo = esPassword ? 'Cambiar contraseña' : modulo.etiqueta;

  return (
    <div className="consola min-h-dvh bg-[var(--color-lienzo)] font-sans text-gray-800">
      {/* El primer tabulador de la página. Sin esto, llegar a la tabla con
          el teclado obliga a recorrer los catorce módulos del menú, y hay
          que hacerlo OTRA VEZ en cada pantalla. */}
      <a href="#contenido" className="salto-contenido">Saltar al contenido</a>

      <BarraLateral
        grupos={grupos}
        activa={activa}
        onSelect={seleccionar}
        abierta={cajon}
        onCerrar={() => setCajon(false)}
        onPassword={() => seleccionar('__password')}
        onSalir={() => decidirSalida({
          contar: () => contarTodo(usuario).then((c) => c.pendientes),
          cerrar: () => cerrarSesion('A_MANO'),
          preguntar: (n) => { setSalidaConPendientes(n); setCajon(false); },
        })}
        onReportar={() => { setReportar(true); setCajon(false); }}
      />

      <div className="md:ml-60 flex flex-col min-h-dvh">
        <Encabezado
          titulo={titulo}
          onMenu={() => setCajon(true)}
          usuario={usuario}
          online={online}
          enCola={enCola}
          onIr={seleccionar}
          onReportar={() => setReportar(true)}
        />

        {/* SESIÓN DE SOPORTE DE TEGO (R-SUP, 20 sep 2026; carril consola,
            montada aquí por el integrador): si quien está dentro es soporte
            entrando como el dueño, se dice arriba todo el rato. Sin sesión
            de soporte no pinta nada. */}
        <CintaSoporte />
        {/* EL AVISO DE MANTENIMIENTO (carril consola, Entrega B): solo si hay
            un corte programado; se cierra y vuelve cuando falte una hora. */}
        <BandaMantenimiento />

        {/* LA LICENCIA VA ANTES QUE LA CONEXIÓN a propósito: quedarse sin
            red es pasajero y se arregla solo; quedarse en modo consulta
            no, y cambia lo que esa persona puede hacer hoy. */}
        <AvisoLicencia
          licencia={aviso?.licencia}
          actualizacion={aviso?.actualizacion}
          empresa={aviso?.empresa}
        />

        {/* DEGRADACIÓN DIGNA · F1·A3. Si un servicio secundario está en pausa
            —o la base no responde— se dice aquí en una línea, y la app sigue.
            Nunca una pantalla muerta. */}
        <AvisoServicios />

        {/* INSTALA TEGO EN TU TELÉFONO · DIRECTRIZ-PWA F1 (11 oct 2026). Una
            línea, solo en teléfono y solo si no está instalada; «Ahora no»
            la guarda 14 días. Montado aquí por el integrador. */}
        <AvisoInstalar irA={seleccionar} activa={activa} />

        {/* Se pierde la conexión sin que nadie pulse nada: si no se anuncia,
            quien no ve la pantalla sigue escaneando creyendo que se guarda.
            DIRECTRIZ-PWA F2 (DISENO-F2 §5): la franja cuenta TODO lo
            pendiente —movimientos, conteos, recepciones, lecturas,
            traslados, ventas, capturas— y lleva a «Pendientes de enviar».
            Botón de 44 px y `flex-wrap` para 320 px. */}
        {!online && (
          <div role="status" className="bg-red-600 text-white text-sm px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="min-w-0 flex-1 basis-56">
              <strong>Sin señal.</strong>{' '}
              {enCola > 0 ? (
                <>
                  <strong>{enCola === 1 ? '1 pendiente de enviar' : `${enCola} pendientes de enviar`}</strong>
                  {enCola === 1 ? ', guardado en este teléfono. Se envía solo al volver la señal.' : ', guardados en este teléfono. Se envían solos al volver la señal.'}
                </>
              ) : 'Lo que hagas se guarda en este teléfono y se envía al volver la señal.'}
            </span>
            {enCola > 0 && (
              <button type="button" onClick={() => seleccionar('pendientes-de-enviar')}
                      className="min-h-11 px-3 rounded-lg bg-white/15 hover:bg-white/25 font-semibold focus:outline-none focus:ring-2 focus:ring-white">
                Ver pendientes →
              </button>
            )}
          </div>
        )}
        <AvisoRechazados rechazados={rechazados} onDescartar={descartarRechazados} />
        {/* Lo que el servidor no aceptó de lo guardado sin señal (colas
            nuevas y espejo de las viejas). Solo cuando `AvisoRechazados` no
            está a la vista, para no decirlo dos veces. */}
        {online && porRevisar > 0 && rechazados.length === 0 && !porRevisarCerrado && (
          <div role="status" className="bg-amber-100 text-amber-900 text-sm px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="min-w-0 flex-1 basis-56">
              <strong>{porRevisar} por revisar</strong>: el servidor no aceptó algo que guardaste sin señal.
            </span>
            <button type="button" onClick={() => seleccionar('pendientes-de-enviar')}
                    className="min-h-11 px-3 rounded-lg bg-amber-200/70 hover:bg-amber-200 font-semibold focus:outline-none focus:ring-2 focus:ring-amber-700">
              Ver qué pasó →
            </button>
            <button type="button" onClick={() => setPorRevisarCerrado(true)} aria-label="Cerrar este aviso"
                    className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-700">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {/* UN MÓDULO EN CONSULTA SE DICE ANTES DE LLENAR NADA · COBRO-CLIP
            B2 (carril escala; montado aquí por el integrador el 14 de
            septiembre de 2026). El servidor niega la escritura con 403;
            este cartel lo cuenta arriba de la pantalla y lleva a pedirlo. */}
        {!esPassword && <CartelDeModulo pantalla={modulo.id} onIr={seleccionar} />}

        {/* EL AVISO DE PRIVACIDAD QUE FALTA POR ACEPTAR · CUMPLIMIENTO F5
            (carril cumplimiento; montado aquí por el integrador el 14 de
            septiembre de 2026). Solo aparece cuando el dueño de TEGO
            declaró una versión vigente y esta persona no la ha aceptado. */}
        {!esPassword && <CartelDeAviso onIr={seleccionar} />}

        {/* EL AVISO DE QUE LA SESIÓN SE VA A CERRAR · deuda T-b.
            Va aquí y no dentro de la pantalla porque es de la sesión
            entera: sale igual se esté donde se esté. Y va FIJO abajo,
            para no empujar el contenido de quien está a media tarea. */}
        <AvisoSesion
          segundosRestantes={segundosRestantes}
          onSeguirAqui={seguirAqui}
        />
        <AvisoVersionNueva />

        {/* `tabIndex={-1}` para que el salto de arriba pueda dejar aquí el
            foco: sin él el navegador mueve la vista pero no el teclado, y el
            siguiente tabulador vuelve al menú.
            P17 · M6 (29 sep 2026): `pb-24` (96 px) deja el final de cada
            pantalla POR ENCIMA de los flotantes de abajo —«Pedir ayuda» y
            «Preguntar», `bottom-4` y 48 px de alto, 64 px en total—. Sin
            él, la última fila de la lista de etiquetas quedaba debajo de
            «Pedir ayuda» y el toque caía en el flotante (LISTO j, hallazgo 4). */}
        <main id="contenido" tabIndex={-1} className="flex-1 p-4 md:p-6 pb-24 md:pb-24 max-w-6xl w-full mx-auto focus:outline-none">
          {esPassword ? (
            <CambioPassword forzado={false} />
          ) : (
            // La clave incluye el contexto para que volver a buscar el mismo
            // módulo con otro SKU remonte la pantalla en vez de ignorarlo.
            // `visibles` (P17): los módulos que esta persona puede abrir, para
            // que un atajo del inicio no lleve a una pantalla que no es suya.
            <Pantalla key={`${modulo.id}:${JSON.stringify(contexto)}`} irA={seleccionar} contexto={contexto}
                      visibles={visibles.map((m) => m.id)} />
          )}
        </main>
      </div>

      {/* A un toque desde cualquier pantalla. Solo si el rol lo tiene:
          el filtro del menú es comodidad, pero un botón que lleva a un
          módulo que no existe para ti es una promesa rota. */}
      {visibles.some((m) => m.id === 'asistente') && (
        <BotonAsistente
          activo={activa === 'asistente'}
          onAbrir={() => seleccionar('asistente')}
        />
      )}

      {/* PEDIR AYUDA A TI (DIRECTRIZ-TICKETS F2). En pantalla ancha se pide
          desde la cabecera y el menú; el flotante queda SOLO en el teléfono
          (`md:hidden`), abajo a la izquierda, para no pisar «Preguntar» ni la
          barra lateral (hallazgo del dueño, 26 sep 2026). Un solo modal,
          compartido. Todos pueden reportar (`tickets.reportar`, de fábrica). */}
      <BotonReportar onAbrir={() => setReportar(true)} />
      {reportar && (
        <Reportar onCerrar={() => setReportar(false)} onIr={seleccionar} />
      )}
      {salidaConPendientes > 0 && (
        <SalidaConPendientes
          pendientes={salidaConPendientes}
          onCerrarSesion={() => cerrarSesion('A_MANO')}
          onQuedarme={() => setSalidaConPendientes(0)}
        />
      )}
    </div>
  );
}
